import React from 'react';
import { Info, Activity, Flame, Calculator, Apple, Salad, Utensils, FileText } from 'lucide-react';

/**
 * Компонент с информационным контентом о TDEE и правильном питании
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-emerald-500" />
        Всё о метаболизме и энергетическом балансе
      </h2>
      
      <div className="prose prose-emerald dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-emerald-500" />
          Что такое TDEE?
        </h3>
        <p>
          <strong>TDEE (Total Daily Energy Expenditure)</strong> — это общий суточный расход энергии, то есть количество калорий, которое ваш организм тратит за день. TDEE складывается из нескольких компонентов:
        </p>
        <ul>
          <li><strong>BMR (Базовый метаболизм)</strong> — энергия, необходимая организму для поддержания основных жизненных функций в состоянии покоя: работы органов, дыхания, кровообращения, поддержания температуры тела.</li>
          <li><strong>TEF (Термический эффект пищи)</strong> — энергия, затрачиваемая на переваривание, усвоение и хранение питательных веществ.</li>
          <li><strong>EAT (Энергия активности)</strong> — энергия, расходуемая на произвольную физическую активность: тренировки, прогулки, работа по дому.</li>
          <li><strong>NEAT (Не связанная с физическими упражнениями активность)</strong> — энергия, расходуемая на непроизвольные движения: мимика, осанка, непроизвольные движения.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-emerald-500" />
          Формулы расчета TDEE
        </h3>
        <p>
          Существует несколько формул для расчета базового метаболизма (BMR), которые затем используются для определения TDEE:
        </p>
        <h4>Формула Миффлина-Сан Жеора (наиболее точная для большинства людей)</h4>
        <ul>
          <li><strong>Для мужчин:</strong> BMR = 10 × вес (кг) + 6.25 × рост (см) - 5 × возраст (лет) + 5</li>
          <li><strong>Для женщин:</strong> BMR = 10 × вес (кг) + 6.25 × рост (см) - 5 × возраст (лет) - 161</li>
        </ul>
        
        <h4>Формула Харриса-Бенедикта (классическая)</h4>
        <ul>
          <li><strong>Для мужчин:</strong> BMR = 88.362 + (13.397 × вес в кг) + (4.799 × рост в см) - (5.677 × возраст)</li>
          <li><strong>Для женщин:</strong> BMR = 447.593 + (9.247 × вес в кг) + (3.098 × рост в см) - (4.330 × возраст)</li>
        </ul>
        
        <h4>Формула Катч-МакАрдла (учитывает процент жира)</h4>
        <p>BMR = 370 + (21.6 × безжировая масса тела в кг)</p>
        <p>Где безжировая масса = вес тела - (вес тела × процент жира / 100)</p>
        
        <p>
          После расчета BMR, для определения TDEE полученное значение умножается на коэффициент активности:
        </p>
        <ul>
          <li><strong>Сидячий образ жизни (минимальная активность):</strong> BMR × 1.2</li>
          <li><strong>Легкая активность (1-3 дня в неделю):</strong> BMR × 1.375</li>
          <li><strong>Умеренная активность (3-5 дней в неделю):</strong> BMR × 1.55</li>
          <li><strong>Высокая активность (6-7 дней в неделю):</strong> BMR × 1.725</li>
          <li><strong>Очень высокая активность (профессиональный спорт):</strong> BMR × 1.9</li>
        </ul>

        <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-100 dark:border-emerald-800 my-6">
          <h4 className="text-emerald-700 dark:text-emerald-300 font-medium">Особенности метаболизма для жителей России</h4>
          <p className="text-emerald-600 dark:text-emerald-400 text-sm mb-2">
            В России, особенно в регионах с холодным климатом, базовый метаболизм может быть выше на 5-10% по сравнению со средними показателями. Это связано с адаптацией организма к холодному климату и необходимостью тратить дополнительную энергию на терморегуляцию.
          </p>
          <p className="text-emerald-600 dark:text-emerald-400 text-sm mb-0">
            Исследования показывают, что у жителей северных регионов России базовый метаболизм может быть выше на 7-8%, чем у жителей южных стран с аналогичными физическими параметрами.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Flame className="w-5 h-5 text-emerald-500" />
          Энергетический баланс и изменение веса
        </h3>
        <p>
          Ваш вес определяется энергетическим балансом — соотношением потребляемых и расходуемых калорий:
        </p>
        <ul>
          <li><strong>Энергетический баланс (поддержание веса):</strong> потребление калорий = TDEE</li>
          <li><strong>Отрицательный баланс (похудение):</strong> потребление калорий &lt; TDEE</li>
          <li><strong>Положительный баланс (набор веса):</strong> потребление калорий &gt; TDEE</li>
        </ul>
        <p>
          Для изменения веса рекомендуется создавать умеренный дефицит или профицит калорий:
        </p>
        <ul>
          <li>Для плавного похудения: дефицит 10-20% от TDEE (около 500-1000 ккал в день)</li>
          <li>Для набора мышечной массы: профицит 10-15% от TDEE (около 250-500 ккал в день)</li>
        </ul>
        <p>
          <strong>Важно:</strong> 1 кг жировой ткани эквивалентен примерно 7700 ккал. Это означает, что для потери 1 кг жира нужно создать дефицит в 7700 ккал, а для набора 1 кг мышечной массы потребуется примерно 3500-4000 ккал (с учетом тренировок).
        </p>

        <h3 className="flex items-center gap-2">
          <Apple className="w-5 h-5 text-emerald-500" />
          Макронутриенты и их роль
        </h3>
        <p>
          Калории в пище поступают из трех основных макронутриентов:
        </p>
        <ul>
          <li><strong>Белки:</strong> 4 ккал/г — строительный материал для тканей, особенно мышц. Помогают сохранять чувство сытости и требуют больше энергии для переваривания.</li>
          <li><strong>Углеводы:</strong> 4 ккал/г — основной источник энергии для мозга и интенсивных тренировок. Делятся на простые (быстрые) и сложные (медленные).</li>
          <li><strong>Жиры:</strong> 9 ккал/г — самый концентрированный источник энергии, необходимы для работы гормональной системы и усвоения жирорастворимых витаминов.</li>
        </ul>
        <p>
          Рекомендуемое распределение макронутриентов зависит от ваших целей:
        </p>
        <ul>
          <li><strong>Для похудения:</strong> Белки 30-40%, Жиры 25-35%, Углеводы 30-40%</li>
          <li><strong>Для набора мышечной массы:</strong> Белки 25-35%, Жиры 20-30%, Углеводы 40-60%</li>
          <li><strong>Для поддержания веса:</strong> Белки 20-30%, Жиры 25-35%, Углеводы 40-50%</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Salad className="w-5 h-5 text-emerald-500" />
          Здоровые пищевые привычки в России
        </h3>
        <p>
          Традиционная русская кухня может быть адаптирована для здорового питания:
        </p>
        <ul>
          <li><strong>Увеличьте потребление супов</strong> — они помогают контролировать аппетит и обеспечивают хорошее насыщение при низкой калорийности.</li>
          <li><strong>Делайте акцент на овощах</strong> — винегреты, салаты из свежих овощей, тушеные овощи богаты клетчаткой и микроэлементами.</li>
          <li><strong>Выбирайте цельнозерновые продукты</strong> — каши из гречки, овса, пшена вместо белого хлеба и макарон.</li>
          <li><strong>Потребляйте качественные белки</strong> — нежирное мясо, рыба, творог, яйца.</li>
          <li><strong>Используйте ферментированные продукты</strong> — квашеная капуста, кефир, ряженка полезны для микробиома кишечника.</li>
          <li><strong>Сократите добавленный сахар и избыточные жиры</strong> — ограничьте потребление выпечки, жареных блюд, сладостей.</li>
        </ul>
        
        <h3 className="flex items-center gap-2">
          <Utensils className="w-5 h-5 text-emerald-500" />
          Практические советы по питанию
        </h3>
        <ul>
          <li><strong>Планируйте приемы пищи заранее</strong> — это помогает избежать импульсивных перекусов и переедания.</li>
          <li><strong>Контролируйте размеры порций</strong> — используйте меньшие тарелки и весы для пищи.</li>
          <li><strong>Включайте белок в каждый прием пищи</strong> — это увеличивает насыщение и помогает сохранять мышечную массу.</li>
          <li><strong>Пейте достаточно воды</strong> — часто чувство голода маскирует жажду.</li>
          <li><strong>Ешьте медленно</strong> — сигнал о насыщении доходит до мозга примерно через 20 минут после начала еды.</li>
          <li><strong>Увеличьте потребление клетчатки</strong> — это обеспечивает длительное чувство сытости и поддерживает здоровье кишечника.</li>
          <li><strong>Ограничьте обработанные продукты</strong> — они часто содержат скрытые сахара, трансжиры и избыток соли.</li>
          <li><strong>Следите за микроэлементами</strong> — особенно в России важны витамины D, B12, йод, железо и селен.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-emerald-500" />
          Документирование и отслеживание
        </h3>
        <p>
          Ведение дневника питания и регулярное взвешивание — эффективные инструменты для контроля энергетического баланса:
        </p>
        <ul>
          <li><strong>Записывайте потребление пищи</strong> — используйте приложения или блокнот для учета калорий и макронутриентов.</li>
          <li><strong>Взвешивайтесь в одинаковых условиях</strong> — лучше всего утром, после посещения туалета и до еды.</li>
          <li><strong>Отслеживайте тренды, а не ежедневные колебания</strong> — вес может меняться на 1-2 кг в течение дня из-за воды, пищи и других факторов.</li>
          <li><strong>Делайте замеры тела</strong> — иногда объемы меняются, хотя вес остается прежним.</li>
          <li><strong>Корректируйте план питания каждые 2-4 недели</strong> — по мере изменения веса меняется и TDEE.</li>
        </ul>

        <p className="font-semibold mt-6">
          Помните, что правильное питание — это не временная диета, а образ жизни. Устойчивые результаты приходят с последовательными, умеренными изменениями, которые вы можете поддерживать долгое время.
        </p>
      </div>
    </article>
  );
};

export default InfoContent;